import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';

/**
 * Landing point for the Google OAuth round trip.
 *
 * The auth service finishes Google sign-in by redirecting to
 * `{origin}/google-auth-success?token=...&refresh=...`.
 *
 * The subtle part: storing the raw token in localStorage is not enough. The
 * app's route guard reads zustand's persisted `pv-admin-auth` state, so a
 * token alone left the guard seeing an unauthenticated visitor and bouncing
 * straight back to /login. This mirrors what the password login does: store
 * the token, fetch /admin/me, then set the store so the guard agrees.
 */
const GoogleAuthSuccessPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState('Signing you in...');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const token = params.get('token');
      const refresh = params.get('refresh');
      const oauthError = params.get('error');

      if (oauthError) {
        setMessage(`Google sign-in failed: ${oauthError}. Try again from the login page.`);
        return;
      }
      if (!token) {
        setMessage('Google did not return a sign-in token. Try again from the login page.');
        return;
      }

      // The store checks the role (SUPER_ADMIN only), stores the token, then
      // reads /admin/me so the route guard and the header agree.
      try {
        await useAuthStore.getState().acceptToken(token, refresh);
        if (cancelled) return;
        navigate('/', { replace: true });
      } catch (e) {
        setMessage(
          'Signed in with Google, but this account cannot open the admin. ' +
            (e instanceof Error ? e.message : ''),
        );
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [params, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-900 px-6">
      <div className="max-w-md text-center">
        <p className="text-surface-700 dark:text-surface-200">{message}</p>
        <button
          type="button"
          onClick={() => navigate('/login', { replace: true })}
          className="mt-6 text-sm text-primary-600 dark:text-primary-400 hover:underline"
        >
          Back to sign in
        </button>
      </div>
    </div>
  );
};

export default GoogleAuthSuccessPage;
